import { mdiClose, mdiPlus } from '@mdi/js';
import Botao from '../Botao';
import HBox from '../layout/HBox';
import VBox from '../layout/VBox';
import CampoTexto from './CampoTexto';

export type ColunaListaObjetos<T> = {
    /** Texto do label exibido acima do campo. */
    label: string;
    /** Nome do campo no objeto. */
    campo: keyof T & string;
    placeholder?: string;
    /** Proporção flex (padrão: 1). Ignorado quando `width` é definido. */
    flex?: number;
    /** Largura fixa (substitui flex). Ex: '120px'. */
    width?: string;
    /** Tipo do input (padrão: 'text'). */
    type?: string;
};

type Props<T extends Record<string, string>> = {
    /** Label exibido no cabeçalho do bloco. */
    label?: string;
    value: T[];
    onChange: (value: T[]) => void;
    /** Definição das colunas a renderizar por item. */
    colunas: ColunaListaObjetos<T>[];
    /** Objeto em branco utilizado ao criar uma nova linha. */
    novoItem: T;
    /** Texto do botão de adicionar (padrão: 'Adicionar'). */
    textoBotaoAdicionar?: string;
    /** Mensagem exibida quando a lista está vazia. */
    textoVazio?: string;
};

export default function CampoListaObjetos<T extends Record<string, string>>({
    label,
    value,
    onChange,
    colunas,
    novoItem,
    textoBotaoAdicionar = 'Adicionar',
    textoVazio = 'Nenhum item configurado.',
}: Props<T>) {
    const adicionar = () => onChange([...value, { ...novoItem }]);

    const remover = (idx: number) =>
        onChange(value.filter((_, i) => i !== idx));

    const atualizar = (idx: number, campo: keyof T & string, v: string) => {
        const next = [...value];
        next[idx] = { ...next[idx], [campo]: v };
        onChange(next);
    };

    return (
        <VBox style={{ gap: '8px' }}>
            <HBox style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                {label && (
                    <label style={{ fontWeight: 500, fontSize: '14px' }}>{label}</label>
                )}
                <Botao size="sm" variant="outline-primary" icon={mdiPlus} onClick={adicionar}>
                    {textoBotaoAdicionar}
                </Botao>
            </HBox>

            {value.length === 0 && (
                <p style={{ fontSize: '13px', color: 'var(--color-muted)', margin: 0 }}>
                    {textoVazio}
                </p>
            )}

            {value.map((item, idx) => (
                <HBox key={idx} style={{ gap: '8px', alignItems: 'flex-end' }}>
                    {colunas.map((col) => (
                        <div
                            key={col.campo}
                            style={col.width ? { width: col.width } : { flex: col.flex ?? 1 }}
                        >
                            <CampoTexto
                                label={col.label}
                                value={item[col.campo] ?? ''}
                                onChange={(v: string) => atualizar(idx, col.campo, v)}
                                placeholder={col.placeholder}
                                type={col.type ?? 'text'}
                            />
                        </div>
                    ))}
                    <Botao
                        size="sm"
                        variant="outline-danger"
                        icon={mdiClose}
                        onClick={() => remover(idx)}
                        style={{ marginBottom: '2px' }}
                        title="Remover"
                    />
                </HBox>
            ))}
        </VBox>
    );
}
